import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  ArrowUpIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  PencilIcon,
  XMarkIcon,
  CheckIcon,
  XCircleIcon,
  LinkIcon,
  EnvelopeIcon,
  SparklesIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import DomainPurchaseRetryModal from '../components/DomainPurchaseRetryModal';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const StatusBadge = ({ status, cancelAtPeriodEnd }) => {
  const isCanceling = status === 'active' && cancelAtPeriodEnd;

  if (isCanceling) {
    return (
      <span className="px-3 py-1 text-sm font-semibold rounded-full bg-orange-100 text-orange-800">
        Canceling
      </span>
    );
  }

  const styles = {
    active: 'bg-green-100 text-green-800',
    past_due: 'bg-yellow-100 text-yellow-800',
    canceled: 'bg-red-100 text-red-800',
    suspended: 'bg-gray-100 text-gray-800',
  };

  return (
    <span className={`px-3 py-1 text-sm font-semibold rounded-full ${styles[status] || styles.suspended}`}>
      {status}
    </span>
  );
};

const PLAN_TO_COMPATIBLE_SITE_TYPES = {
  pro: ['marketing'],
  landing_page: ['landing_page'],
  ecommerce: ['ecommerce', 'catalog'],
};

const PLAN_TYPE_LABELS = {
  pro: 'Marketing',
  landing_page: 'Landing Page',
  ecommerce: 'eCommerce',
};

// Human-readable labels for the structured cancellation reasons captured
// from the dashboard's cancel confirmation modal. Keep in sync with
// ALLOWED_CANCELLATION_REASONS in server/routes/subscriptions.js and the
// i18n keys under billing.cancelFlow.reasons.* in client/src/i18n/locales.
//
// `plan_upgrade` is a server-only sentinel stamped by /cancel-for-upgrade
// when a user trades a lower-tier sub for a higher one. The user can
// never select it themselves -- it exists purely to distinguish retained
// (upgraded) revenue from real churn in the backoffice. The detail page
// renders it as a "Plan Upgrade" panel (not a churn cancellation), see
// `isPlanUpgradeCancellation` below.
const CANCELLATION_REASON_LABELS = {
  site_quality: 'The site is not good enough',
  better_alternatives: 'Better platforms / solutions',
  too_expensive: 'Too expensive',
  too_buggy: 'Too many bugs / not stable',
  missing_features: 'Missing features',
  no_longer_needed: "Doesn't need a website anymore",
  temporary_break: 'Temporary break',
  other: 'Other',
  plan_upgrade: 'Upgraded to a higher plan',
};

// Parse the JSON metadata that /cancel-for-upgrade packs into
// `cancellation_reason_details`. Returns null on parse failure or empty
// input so older rows (before the marker existed) and any future
// non-JSON payloads degrade gracefully to the generic reason panel.
const parsePlanUpgradeDetails = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch (_e) {
    // Not JSON -- fall through and let the caller render the raw string.
  }
  return null;
};

const PlanBadge = ({ planType, isBundle }) => {
  const styles = {
    pro: 'bg-blue-100 text-blue-800',
    ecommerce: 'bg-purple-100 text-purple-800',
    landing_page: 'bg-green-100 text-green-700',
  };
  const label = isBundle
    ? (planType === 'pro'
      ? 'Marketing Bundle'
      : planType === 'ecommerce'
        ? 'Ecommerce Bundle'
        : `${PLAN_TYPE_LABELS[planType] || planType} Bundle`)
    : (PLAN_TYPE_LABELS[planType] || planType);
  return (
    <span className={`px-3 py-1 text-sm font-semibold rounded-full ${styles[planType] || 'bg-gray-100 text-gray-800'}`}>
      {label}
    </span>
  );
};

const SITE_TYPE_LABELS = {
  marketing: 'Marketing',
  landing_page: 'Landing Page',
  ecommerce: 'eCommerce',
  catalog: 'Catalog',
};

// Human-readable labels for the per-site add-on subscriptions (the
// `feature_subscriptions` table). New add-ons should be added here so the
// detail page renders a friendly title instead of the raw column value.
const FEATURE_LABELS = {
  newsletter: 'Newsletter',
  crm: 'CRM',
  whatsapp_bot: 'WhatsApp Bot',
};

// Lifecycle status colors for an `email_mailboxes` row. The Zappy-side
// `status` column drives the badge (provider_status mirrors OpenSRS state
// and is shown separately as a small caption). New states added to
// mailboxService.js should be reflected here.
const MAILBOX_STATUS_STYLES = {
  active: 'bg-green-100 text-green-700',
  payment_pending: 'bg-blue-100 text-blue-700',
  payment_confirmed: 'bg-blue-100 text-blue-700',
  provisioning: 'bg-yellow-100 text-yellow-700',
  past_due: 'bg-yellow-100 text-yellow-700',
  failed: 'bg-red-100 text-red-700',
  suspended: 'bg-gray-200 text-gray-700',
  canceled: 'bg-red-100 text-red-700',
};

const MailboxStatusBadge = ({ status }) => (
  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${MAILBOX_STATUS_STYLES[status] || 'bg-gray-100 text-gray-700'}`}>
    {status || 'unknown'}
  </span>
);

const FeatureSubscriptionStatusBadge = ({ addon }) => {
  const isCanceling = addon.status === 'active' && addon.cancel_at_period_end;
  if (isCanceling) {
    return (
      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
        Canceling
      </span>
    );
  }
  const styles = {
    active: 'bg-green-100 text-green-800',
    past_due: 'bg-yellow-100 text-yellow-800',
    canceled: 'bg-red-100 text-red-800',
  };
  return (
    <span
      className={`px-2 py-0.5 text-xs font-semibold rounded-full ${styles[addon.status] || 'bg-gray-100 text-gray-700'}`}
    >
      {addon.status}
    </span>
  );
};

const isWebsiteCompatible = (planType, siteType) => {
  const compatibleTypes = PLAN_TO_COMPATIBLE_SITE_TYPES[planType] || [];
  return compatibleTypes.includes(siteType || 'marketing');
};

const InfoField = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-500 mb-1">{label}</label>
    <div className="text-gray-900">{children}</div>
  </div>
);

const SubscriptionDetail = () => {
  const { subscriptionId } = useParams();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    plan_type: '',
    billing_period: '',
    status: '',
    amount: '',
  });
  const [linkingWebsite, setLinkingWebsite] = useState(false);
  const [accountWebsites, setAccountWebsites] = useState([]);
  const [selectedWebsiteId, setSelectedWebsiteId] = useState('');
  const [linkingAccount, setLinkingAccount] = useState(false);
  const [accountSearchQuery, setAccountSearchQuery] = useState('');
  const [accountSearchResults, setAccountSearchResults] = useState([]);
  const [accountSearchLoading, setAccountSearchLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [domainRetryOpen, setDomainRetryOpen] = useState(false);
  const [loadingWebsites, setLoadingWebsites] = useState(false);
  // Mailbox id currently being re-provisioned (per-row loading state).
  const [retryingMailboxId, setRetryingMailboxId] = useState(null);
  const accountSearchDebounceRef = useRef(null);
  const accountDropdownRef = useRef(null);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/subscriptions/${subscriptionId}`);
      setSubscription(response.data.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      toast.error('Failed to fetch subscription details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionId]);

  // Re-trigger OpenSRS Hosted Email provisioning for a mailbox stuck in
  // failed/payment_confirmed/provisioning. Idempotent server-side. A
  // bad-password failure (now surfaced verbatim in error_message via the
  // OpenSRS `hints` logging) will fail the same way — the admin should ask
  // the customer to reset the password rather than just retrying.
  const handleRetryMailbox = async (mailboxId) => {
    if (!mailboxId || retryingMailboxId) return;
    try {
      setRetryingMailboxId(mailboxId);
      const res = await axios.post(
        `${API_BASE_URL}/api/admin/subscriptions/${subscriptionId}/mailboxes/${mailboxId}/retry-provision`
      );
      if (res.data?.success) {
        toast.success('Mailbox provisioned successfully');
      } else {
        toast.success('Retry submitted');
      }
      await fetchSubscription();
    } catch (error) {
      const msg = error.response?.data?.error || error.message || 'Retry failed';
      console.error('Error retrying mailbox provisioning:', error);
      toast.error(`Retry failed: ${msg}`);
      // Refresh so the (possibly updated) error_message / status shows.
      await fetchSubscription();
    } finally {
      setRetryingMailboxId(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this subscription? This will set the status to canceled and notify the user.')) {
      return;
    }
    try {
      setActionLoading('cancel');
      await axios.post(`${API_BASE_URL}/api/admin/subscriptions/${subscriptionId}/cancel`);
      toast.success('Subscription canceled');
      await fetchSubscription();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      toast.error('Failed to cancel subscription');
    } finally {
      setActionLoading(null);
    }
  };

  const startEditing = () => {
    setEditForm({
      plan_type: subscription.plan_type,
      billing_period: subscription.billing_period,
      status: subscription.status,
      amount: parseFloat(subscription.amount).toString(),
    });
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
  };

  const handleSaveEdit = async () => {
    try {
      setActionLoading('edit');
      const updates = {};
      if (editForm.plan_type !== subscription.plan_type) updates.plan_type = editForm.plan_type;
      if (editForm.billing_period !== subscription.billing_period) updates.billing_period = editForm.billing_period;
      if (editForm.status !== subscription.status) updates.status = editForm.status;
      if (parseFloat(editForm.amount) !== parseFloat(subscription.amount)) updates.amount = parseFloat(editForm.amount);

      if (Object.keys(updates).length === 0) {
        toast('No changes to save');
        setEditing(false);
        return;
      }

      await axios.put(`${API_BASE_URL}/api/admin/subscriptions/${subscriptionId}`, updates);
      toast.success('Subscription updated');
      setEditing(false);
      await fetchSubscription();
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast.error('Failed to update subscription');
    } finally {
      setActionLoading(null);
    }
  };

  const startLinkingWebsite = useCallback(async () => {
    setLinkingWebsite(true);
    setLoadingWebsites(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/subscriptions/${subscriptionId}/account-websites`);
      setAccountWebsites(response.data.data || []);
      setSelectedWebsiteId(subscription?.website_id || '');
    } catch (error) {
      console.error('Error fetching account websites:', error);
      toast.error('Failed to load account websites');
      setLinkingWebsite(false);
    } finally {
      setLoadingWebsites(false);
    }
  }, [subscriptionId, subscription?.website_id]);

  const handleSaveWebsiteLink = async () => {
    try {
      setActionLoading('link-website');
      await axios.put(`${API_BASE_URL}/api/admin/subscriptions/${subscriptionId}`, {
        website_id: selectedWebsiteId || null,
      });
      toast.success(selectedWebsiteId ? 'Website linked to subscription' : 'Website unlinked from subscription');
      setLinkingWebsite(false);
      await fetchSubscription();
    } catch (error) {
      console.error('Error linking website:', error);
      toast.error(error.response?.data?.error || 'Failed to update website link');
    } finally {
      setActionLoading(null);
    }
  };

  const searchAccounts = useCallback(async (query) => {
    if (query.trim().length < 1) {
      setAccountSearchResults([]);
      setShowAccountDropdown(false);
      return;
    }
    try {
      setAccountSearchLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/accounts/search`, {
        params: { q: query },
      });
      const results = response.data.data || [];
      setAccountSearchResults(results);
      setShowAccountDropdown(results.length > 0);
    } catch (error) {
      console.error('Error searching accounts:', error);
      setAccountSearchResults([]);
      setShowAccountDropdown(false);
    } finally {
      setAccountSearchLoading(false);
    }
  }, []);

  const startLinkingAccount = () => {
    setLinkingAccount(true);
    setSelectedAccount(
      subscription?.account_id
        ? {
            id: subscription.account_id,
            name: subscription.account_name || 'Current account',
            tier: subscription.account_tier,
          }
        : null
    );
    setAccountSearchQuery(
      subscription?.account_name
        ? `${subscription.account_name}${subscription.account_tier ? ` (${subscription.account_tier})` : ''}`
        : ''
    );
    setAccountSearchResults([]);
    setShowAccountDropdown(false);
  };

  const handleAccountSearchChange = (e) => {
    const value = e.target.value;
    setAccountSearchQuery(value);
    setSelectedAccount(null);
    if (accountSearchDebounceRef.current) clearTimeout(accountSearchDebounceRef.current);
    accountSearchDebounceRef.current = setTimeout(() => searchAccounts(value), 300);
  };

  const handleSelectAccount = (account) => {
    setSelectedAccount(account);
    setAccountSearchQuery(
      `${account.name || 'Unnamed Account'}${account.tier ? ` (${account.tier})` : ''}${account.primaryEmail ? ` — ${account.primaryEmail}` : ''}`
    );
    setShowAccountDropdown(false);
  };

  const handleSaveAccountLink = async () => {
    if (!selectedAccount?.id) {
      toast.error('Select an account first');
      return;
    }
    try {
      setActionLoading('link-account');
      await axios.put(`${API_BASE_URL}/api/admin/subscriptions/${subscriptionId}`, {
        account_id: selectedAccount.id,
      });
      toast.success('Account linked to subscription');
      setLinkingAccount(false);
      await fetchSubscription();
    } catch (error) {
      console.error('Error linking account:', error);
      toast.error(error.response?.data?.error || 'Failed to update account link');
    } finally {
      setActionLoading(null);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(e.target)) {
        setShowAccountDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <LoadingSpinner />
      </AdminLayout>
    );
  }

  if (!subscription) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">Subscription not found</p>
          <button
            onClick={() => navigate('/subscriptions')}
            className="mt-4 text-primary-600 hover:text-primary-800"
          >
            Back to Subscriptions
          </button>
        </div>
      </AdminLayout>
    );
  }

  const currencySymbol = subscription.currency === 'ILS' ? '₪' : '$';
  const isCanceled = subscription.status === 'canceled';
  const isCanceling = subscription.status === 'active' && subscription.cancel_at_period_end;
  const isPlanUpgradeCancellation = subscription.cancellation_reason === 'plan_upgrade';
  const planUpgradeDetails = isPlanUpgradeCancellation
    ? parsePlanUpgradeDetails(subscription.cancellation_reason_details)
    : null;

  return (
    <AdminLayout>
      <div>
        {/* Back navigation */}
        <button
          onClick={() => navigate('/subscriptions')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Subscriptions
        </button>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold text-gray-900">Subscription</h1>
            <PlanBadge planType={subscription.plan_type} isBundle={subscription.is_bundle} />
            <StatusBadge status={subscription.status} cancelAtPeriodEnd={subscription.cancel_at_period_end} />
            {isPlanUpgradeCancellation && (
              <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800">
                <ArrowUpIcon className="w-4 h-4" />
                Upgraded
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {!editing && (
              <button
                onClick={startEditing}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 rounded-lg transition-colors"
              >
                <PencilIcon className="w-4 h-4" />
                Edit
              </button>
            )}
            {!isCanceled && !isCanceling && (
              <button
                onClick={handleCancel}
                disabled={actionLoading === 'cancel'}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <XCircleIcon className="w-4 h-4" />
                {actionLoading === 'cancel' ? 'Canceling...' : 'Cancel Subscription'}
              </button>
            )}
          </div>
        </div>

        {/* Subscription Details */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Subscription Details</h3>

          {editing ? (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan Type</label>
                  <select
                    value={editForm.plan_type}
                    onChange={(e) => setEditForm({ ...editForm, plan_type: e.target.value })}
                    className="input-field w-full"
                  >
                    <option value="pro">Marketing (Pro)</option>
                    <option value="ecommerce">eCommerce</option>
                    <option value="landing_page">Landing Page</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Billing Period</label>
                  <select
                    value={editForm.billing_period}
                    onChange={(e) => setEditForm({ ...editForm, billing_period: e.target.value })}
                    className="input-field w-full"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    className="input-field w-full"
                  >
                    <option value="active">Active</option>
                    <option value="past_due">Past Due</option>
                    <option value="canceled">Canceled</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount ({subscription.currency})</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.amount}
                    onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                    className="input-field w-full"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSaveEdit}
                  disabled={actionLoading === 'edit'}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  <CheckIcon className="w-4 h-4" />
                  {actionLoading === 'edit' ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={cancelEditing}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-4 h-4" />
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <InfoField label="Subscription ID">
                <span className="font-mono text-sm">{subscription.id}</span>
              </InfoField>
              <InfoField label="Plan Type">
                <PlanBadge planType={subscription.plan_type} isBundle={subscription.is_bundle} />
              </InfoField>
              <InfoField label="Billing Period">
                <span className="capitalize">{subscription.billing_period}</span>
              </InfoField>
              <InfoField label="Status">
                <StatusBadge status={subscription.status} cancelAtPeriodEnd={subscription.cancel_at_period_end} />
              </InfoField>
              <InfoField label="Amount">
                <span className="text-lg font-semibold">{currencySymbol}{parseFloat(subscription.amount).toFixed(2)}</span>
                <span className="text-sm text-gray-500 ml-1">{subscription.currency}</span>
              </InfoField>
              <InfoField label="Payment Provider">
                {subscription.payment_provider === 'paypal' ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c1.652 1.857 1.067 4.887.197 8.31-.96 3.773-3.94 5.446-7.476 5.446h-.71l-.96 6.088a.641.641 0 0 1-.633.54H7.716c-.36 0-.624-.332-.567-.688l.143-.907h1.464c.524 0 .968-.382 1.05-.9l1.04-6.583h1.903c4.298 0 7.664-1.747 8.647-6.797.03-.149.054-.294.077-.437.18-1.132.19-2.087-.251-2.931z"/></svg>
                    PayPal
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                    Credit Card (iCount)
                  </span>
                )}
              </InfoField>
              {subscription.payment_provider === 'paypal' ? (
                <InfoField label="PayPal Subscription ID">
                  <span className="font-mono text-sm">{subscription.paypal_subscription_id || '—'}</span>
                </InfoField>
              ) : (
                <InfoField label="iCount Subscription ID">
                  <span className="font-mono text-sm">{subscription.icount_subscription_id || '—'}</span>
                </InfoField>
              )}
              <InfoField label="Current Period Start">
                {subscription.current_period_start
                  ? new Date(subscription.current_period_start).toLocaleString()
                  : 'N/A'}
              </InfoField>
              <InfoField label="Current Period End">
                {subscription.current_period_end
                  ? new Date(subscription.current_period_end).toLocaleString()
                  : 'N/A'}
              </InfoField>
              <InfoField label="Cancel at Period End">
                {subscription.cancel_at_period_end ? (
                  <span className="text-orange-600 font-medium">Yes</span>
                ) : (
                  <span className="text-gray-500">No</span>
                )}
              </InfoField>
              <InfoField label="Created At">
                {subscription.created_at
                  ? new Date(subscription.created_at).toLocaleString()
                  : 'N/A'}
              </InfoField>
              <InfoField label="Updated At">
                {subscription.updated_at
                  ? new Date(subscription.updated_at).toLocaleString()
                  : 'N/A'}
              </InfoField>
              <InfoField label="Billed Site Count">
                {subscription.billed_site_count ?? '—'}
              </InfoField>
            </div>
          )}
        </div>

        {/* Cancellation Reason / Plan Upgrade panel.
            Branches on `cancellation_reason === 'plan_upgrade'` so that
            upgrade-cancels (retained revenue) render as a friendly blue
            "Plan Upgrade" panel with deep links to the new sub's site,
            distinct from the red churn-cancellation panel. The marker
            is stamped server-side by /api/subscriptions/cancel-for-upgrade. */}
        {subscription.cancellation_reason && isPlanUpgradeCancellation ? (
          <div className="bg-white rounded-lg shadow p-6 mb-6 border-l-4 border-blue-500">
            <div className="flex items-center gap-2 mb-4">
              <ArrowUpIcon className="w-5 h-5 text-blue-500" />
              <h3 className="text-xl font-bold text-gray-900">Plan Upgrade</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              This subscription was canceled because the customer upgraded
              to a higher plan. The iCount standing order / PayPal billing
              agreement was terminated at the provider, and a new
              subscription was set up at the higher tier.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <InfoField label="From plan">
                <PlanBadge
                  planType={planUpgradeDetails?.sourcePlan || subscription.plan_type}
                />
              </InfoField>
              <InfoField label="To plan">
                {planUpgradeDetails?.targetPlan ? (
                  <PlanBadge planType={planUpgradeDetails.targetPlan} />
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </InfoField>
              <InfoField label="Canceled At">
                {subscription.cancellation_canceled_at
                  ? new Date(subscription.cancellation_canceled_at).toLocaleString()
                  : subscription.updated_at
                    ? new Date(subscription.updated_at).toLocaleString()
                    : 'N/A'}
              </InfoField>
              {planUpgradeDetails?.websiteId && (
                <InfoField label="Upgraded site">
                  <Link
                    to={`/websites/${planUpgradeDetails.websiteId}`}
                    className="text-primary-600 hover:text-primary-800 font-medium"
                  >
                    Open website
                  </Link>
                </InfoField>
              )}
              {planUpgradeDetails?.billingPeriod && (
                <InfoField label="Billing Period">
                  <span className="capitalize">{planUpgradeDetails.billingPeriod}</span>
                </InfoField>
              )}
              {planUpgradeDetails?.currency && (
                <InfoField label="Currency">
                  <span>{planUpgradeDetails.currency}</span>
                </InfoField>
              )}
            </div>
            {/* Older rows / fallback: render the raw details string when
                we couldn't parse it as JSON, so no information is lost. */}
            {!planUpgradeDetails && subscription.cancellation_reason_details && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-500 mb-1">Details</label>
                <p className="text-gray-900 whitespace-pre-wrap bg-gray-50 rounded-lg p-3 text-sm font-mono break-all">
                  {subscription.cancellation_reason_details}
                </p>
              </div>
            )}
          </div>
        ) : subscription.cancellation_reason ? (
          <div className="bg-white rounded-lg shadow p-6 mb-6 border-l-4 border-red-500">
            <div className="flex items-center gap-2 mb-4">
              <XCircleIcon className="w-5 h-5 text-red-500" />
              <h3 className="text-xl font-bold text-gray-900">Cancellation Reason</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <InfoField label="Reason">
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-800">
                  {CANCELLATION_REASON_LABELS[subscription.cancellation_reason] || subscription.cancellation_reason}
                </span>
              </InfoField>
              <InfoField label="Canceled At">
                {subscription.cancellation_canceled_at
                  ? new Date(subscription.cancellation_canceled_at).toLocaleString()
                  : 'N/A'}
              </InfoField>
            </div>
            {subscription.cancellation_reason_details && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-500 mb-1">User comments</label>
                <p className="text-gray-900 whitespace-pre-wrap bg-gray-50 rounded-lg p-3 text-sm">
                  {subscription.cancellation_reason_details}
                </p>
              </div>
            )}
          </div>
        ) : null}

        {/* Feature (add-on) Subscriptions */}
        {Array.isArray(subscription.feature_subscriptions) &&
          subscription.feature_subscriptions.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <EnvelopeIcon className="w-5 h-5 text-pink-600" />
                <h3 className="text-xl font-bold text-gray-900">Add-on Subscriptions</h3>
                <span className="text-xs text-gray-500">
                  {subscription.feature_subscriptions.length} total
                </span>
              </div>
              <div className="space-y-4">
                {subscription.feature_subscriptions.map((addon) => {
                  const symbol = addon.currency === 'ILS' ? '₪' : '$';
                  return (
                    <div
                      key={addon.id}
                      className="border border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex items-center gap-2 flex-wrap mb-3">
                        <span className="text-base font-semibold text-gray-900">
                          {FEATURE_LABELS[addon.feature] || addon.feature}
                        </span>
                        <FeatureSubscriptionStatusBadge addon={addon} />
                        {addon.cancel_at_period_end && addon.status === 'active' && (
                          <span className="text-xs text-orange-600">
                            ends {addon.current_period_end
                              ? new Date(addon.current_period_end).toLocaleDateString()
                              : '—'}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <InfoField label="Amount">
                          <span className="font-semibold">
                            {symbol}
                            {addon.amount ? parseFloat(addon.amount).toFixed(2) : '—'}
                          </span>
                          <span className="text-sm text-gray-500 ml-1">
                            {addon.currency || ''} / {addon.billing_period || 'monthly'}
                          </span>
                        </InfoField>
                        <InfoField label="Payment Provider">
                          {addon.payment_provider === 'paypal' ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                              PayPal
                            </span>
                          ) : addon.payment_provider === 'icount' ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                              Credit Card (iCount)
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </InfoField>
                        <InfoField label="External Subscription ID">
                          <span className="font-mono text-xs break-all">
                            {addon.paypal_subscription_id ||
                              addon.icount_subscription_id ||
                              '—'}
                          </span>
                        </InfoField>
                        <InfoField label="Current Period Start">
                          {addon.current_period_start
                            ? new Date(addon.current_period_start).toLocaleString()
                            : '—'}
                        </InfoField>
                        <InfoField label="Current Period End">
                          {addon.current_period_end
                            ? new Date(addon.current_period_end).toLocaleString()
                            : '—'}
                        </InfoField>
                        <InfoField label="Cancel at Period End">
                          {addon.cancel_at_period_end ? (
                            <span className="text-orange-600 font-medium">Yes</span>
                          ) : (
                            <span className="text-gray-500">No</span>
                          )}
                        </InfoField>
                        <InfoField label="Created At">
                          {addon.created_at
                            ? new Date(addon.created_at).toLocaleString()
                            : '—'}
                        </InfoField>
                        {addon.canceled_at && (
                          <InfoField label="Canceled At">
                            {new Date(addon.canceled_at).toLocaleString()}
                          </InfoField>
                        )}
                        <InfoField label="Add-on ID">
                          <span className="font-mono text-xs break-all">{addon.id}</span>
                        </InfoField>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        {/* Domain Purchase Details */}
        {subscription.domain_purchase && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <GlobeAltIcon className="w-5 h-5 text-primary-600" />
              <h3 className="text-xl font-bold text-gray-900">Domain Purchase</h3>
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                subscription.domain_purchase.status === 'active' ? 'bg-green-100 text-green-700' :
                subscription.domain_purchase.status === 'failed' ? 'bg-red-100 text-red-700' :
                subscription.domain_purchase.status === 'purchasing' ? 'bg-blue-100 text-blue-700' :
                'bg-yellow-100 text-yellow-700'
              }`}>
                {subscription.domain_purchase.status}
              </span>
              {(subscription.domain_purchase.status === 'failed'
                || subscription.domain_purchase.status === 'payment_confirmed')
                && (subscription.domain_purchase.icount_hk_id
                  || subscription.domain_purchase.paypal_subscription_id
                  || ['icount', 'paypal'].includes(subscription.domain_purchase.payment_provider)) && (
                <button
                  type="button"
                  onClick={() => setDomainRetryOpen(true)}
                  className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg"
                >
                  <ArrowPathIcon className="w-4 h-4" />
                  Retry registration
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <InfoField label="Domain">
                <span className="font-semibold text-gray-900">{subscription.domain_purchase.domain}</span>
              </InfoField>
              <InfoField label="Purchase ID">
                <span className="font-mono text-xs break-all">{subscription.domain_purchase.id}</span>
              </InfoField>
              <InfoField label="TLD">
                <span className="font-mono text-sm">.{subscription.domain_purchase.tld}</span>
              </InfoField>
              <InfoField label="Registrar">
                <span className="capitalize">{subscription.domain_purchase.registrar}</span>
              </InfoField>
              <InfoField label="Price">
                <span className="font-semibold">
                  {subscription.domain_purchase.price_currency === 'ILS' ? '₪' : '$'}
                  {subscription.domain_purchase.price_amount ? parseFloat(subscription.domain_purchase.price_amount).toFixed(2) : '—'}
                </span>
                <span className="text-sm text-gray-500 ml-1">{subscription.domain_purchase.price_currency}/year</span>
              </InfoField>
              <InfoField label="Payment Provider">
                {subscription.domain_purchase.payment_provider === 'paypal' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">PayPal</span>
                ) : subscription.domain_purchase.payment_provider === 'icount' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">Credit Card (iCount)</span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </InfoField>
              <InfoField label="Renewal Status">
                <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                  subscription.domain_purchase.renewal_status === 'active' ? 'bg-green-100 text-green-700' :
                  subscription.domain_purchase.renewal_status === 'canceled' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {subscription.domain_purchase.renewal_status || '—'}
                </span>
              </InfoField>
              {subscription.domain_purchase.registrar_order_id && (
                <InfoField label="Registrar Order ID">
                  <span className="font-mono text-sm">{subscription.domain_purchase.registrar_order_id}</span>
                </InfoField>
              )}
              {subscription.domain_purchase.cloudflare_zone_id && (
                <InfoField label="Cloudflare Zone ID">
                  <span className="font-mono text-sm">{subscription.domain_purchase.cloudflare_zone_id}</span>
                </InfoField>
              )}
              {subscription.domain_purchase.icount_hk_id && (
                <InfoField label="iCount Standing Order ID">
                  <span className="font-mono text-sm">{subscription.domain_purchase.icount_hk_id}</span>
                </InfoField>
              )}
              {subscription.domain_purchase.paypal_subscription_id && (
                <InfoField label="PayPal Subscription ID">
                  <span className="font-mono text-sm">{subscription.domain_purchase.paypal_subscription_id}</span>
                </InfoField>
              )}
              <InfoField label="Next Renewal">
                {subscription.domain_purchase.next_renewal_date
                  ? new Date(subscription.domain_purchase.next_renewal_date).toLocaleDateString()
                  : '—'}
              </InfoField>
              <InfoField label="Purchased At">
                {subscription.domain_purchase.created_at
                  ? new Date(subscription.domain_purchase.created_at).toLocaleString()
                  : '—'}
              </InfoField>
              {subscription.domain_purchase.error_message && (
                <div className="md:col-span-2 lg:col-span-3">
                  <InfoField label="Error">
                    <span className="text-red-600 text-sm">{subscription.domain_purchase.error_message}</span>
                  </InfoField>
                </div>
              )}
            </div>
            {subscription.domain_purchase.contact_info && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm font-medium text-gray-700 mb-2">Registrant Contact</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-gray-600">
                  <div>
                    <span className="text-gray-400">Name:</span>{' '}
                    {subscription.domain_purchase.contact_info.nameFirst} {subscription.domain_purchase.contact_info.nameLast}
                  </div>
                  <div>
                    <span className="text-gray-400">Email:</span>{' '}
                    {subscription.domain_purchase.contact_info.email}
                  </div>
                  {subscription.domain_purchase.contact_info.phone && (
                    <div>
                      <span className="text-gray-400">Phone:</span>{' '}
                      {subscription.domain_purchase.contact_info.phone}
                    </div>
                  )}
                  {subscription.domain_purchase.contact_info.address1 && (
                    <div>
                      <span className="text-gray-400">Address:</span>{' '}
                      {subscription.domain_purchase.contact_info.address1}, {subscription.domain_purchase.contact_info.city} {subscription.domain_purchase.contact_info.postalCode}
                    </div>
                  )}
                  {subscription.domain_purchase.contact_info.country && (
                    <div>
                      <span className="text-gray-400">Country:</span>{' '}
                      {subscription.domain_purchase.contact_info.country}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {Array.isArray(subscription.ai_quota_topups) && subscription.ai_quota_topups.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <SparklesIcon className="w-5 h-5 text-pink-600" />
              <h3 className="text-xl font-bold text-gray-900">AI Quota Top-ups</h3>
              <span className="text-xs text-gray-500">
                {subscription.ai_quota_topups.length} purchase
                {subscription.ai_quota_topups.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="space-y-4">
              {subscription.ai_quota_topups.map((topup) => {
                const symbol = topup.currency === 'ILS' ? '₪' : '$';
                const displayStatus = topup.displayStatus || topup.status || 'active';
                const statusClass = displayStatus === 'active'
                  ? 'bg-green-100 text-green-700'
                  : displayStatus === 'exhausted'
                    ? 'bg-orange-100 text-orange-700'
                    : 'bg-gray-100 text-gray-700';
                return (
                  <div key={topup.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      <span className="text-base font-semibold text-gray-900">
                        {topup.totalCredits} AI credits
                      </span>
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${statusClass}`}>
                        {displayStatus}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <InfoField label="Credits Remaining">
                        <span>{topup.remainingCredits ?? 0} / {topup.totalCredits ?? 0}</span>
                      </InfoField>
                      <InfoField label="Amount">
                        <span className="font-semibold">
                          {symbol}
                          {topup.amount != null ? Number(topup.amount).toFixed(2) : '—'}
                        </span>
                        <span className="text-sm text-gray-500 ml-1">{topup.currency}</span>
                      </InfoField>
                      <InfoField label="Payment Provider">
                        {topup.paymentProvider === 'paypal' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                            PayPal
                          </span>
                        ) : topup.paymentProvider === 'icount' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                            Credit Card (iCount)
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </InfoField>
                      <InfoField label="Purchased At">
                        {topup.purchasedAt
                          ? new Date(topup.purchasedAt).toLocaleString()
                          : '—'}
                      </InfoField>
                      <InfoField label="Expires At">
                        {topup.expiresAt
                          ? new Date(topup.expiresAt).toLocaleString()
                          : '—'}
                      </InfoField>
                      {topup.paypalOrderId && (
                        <InfoField label="PayPal Order ID">
                          <span className="font-mono text-xs break-all">{topup.paypalOrderId}</span>
                        </InfoField>
                      )}
                      {topup.icountDocId && (
                        <InfoField label="iCount Doc ID">
                          <span className="font-mono text-xs break-all">{topup.icountDocId}</span>
                        </InfoField>
                      )}
                      {topup.paymentReference && (
                        <InfoField label="Payment Reference">
                          <span className="font-mono text-xs break-all">{topup.paymentReference}</span>
                        </InfoField>
                      )}
                      <InfoField label="Top-up ID">
                        <span className="font-mono text-xs break-all">{topup.id}</span>
                      </InfoField>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Email Mailboxes (OpenSRS Hosted Email).
            Each mailbox is its own recurring purchase (billing fields inline
            on `email_mailboxes` — NOT in `feature_subscriptions`, because a
            site can have many mailboxes and the partial unique index on
            (website_id, feature) WHERE status='active' would only allow one).
            Includes soft-deleted (`provider_status='deleted'`) rows so admins
            see the full history of provisioning attempts and cancellations.
            See server/routes/admin/subscriptions.js GET /:subscriptionId. */}
        {Array.isArray(subscription.mailboxes) && subscription.mailboxes.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <EnvelopeIcon className="w-5 h-5 text-pink-600" />
              <h3 className="text-xl font-bold text-gray-900">Email Mailboxes</h3>
              <span className="text-xs text-gray-500">
                {subscription.mailboxes.length} total
                {' · '}
                {subscription.mailboxes.filter((m) => m.status === 'active').length} active
              </span>
            </div>
            <div className="space-y-4">
              {subscription.mailboxes.map((mb) => {
                const symbol = mb.currency === 'ILS' ? '₪' : '$';
                const address = `${mb.local_part}@${mb.domain}`;
                return (
                  <div
                    key={mb.id}
                    className={`border rounded-lg p-4 ${mb.provider_status === 'deleted' ? 'border-gray-200 bg-gray-50 opacity-75' : 'border-gray-200'}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      <span className="text-base font-semibold text-gray-900 break-all">{address}</span>
                      <MailboxStatusBadge status={mb.status} />
                      {mb.provider_status && mb.provider_status !== mb.status && (
                        <span className="text-xs text-gray-500">
                          provider: <span className="font-medium">{mb.provider_status}</span>
                        </span>
                      )}
                      {mb.cancel_at_period_end && mb.status === 'active' && (
                        <span className="text-xs text-orange-600">
                          ends {mb.current_period_end
                            ? new Date(mb.current_period_end).toLocaleDateString()
                            : '—'}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <InfoField label="Display Name">
                        <span>{mb.display_name || '—'}</span>
                      </InfoField>
                      <InfoField label="Quota">
                        <span>{mb.quota_mb ? `${mb.quota_mb} MB` : '—'}</span>
                      </InfoField>
                      <InfoField label="Amount">
                        <span className="font-semibold">
                          {symbol}
                          {mb.amount ? parseFloat(mb.amount).toFixed(2) : '—'}
                        </span>
                        <span className="text-sm text-gray-500 ml-1">
                          {mb.currency || ''} / {mb.billing_period || 'monthly'}
                        </span>
                      </InfoField>
                      <InfoField label="Payment Provider">
                        {mb.payment_provider === 'paypal' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                            PayPal
                          </span>
                        ) : mb.payment_provider === 'icount' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                            Credit Card (iCount)
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </InfoField>
                      <InfoField label="External Subscription ID">
                        <span className="font-mono text-xs break-all">
                          {mb.paypal_subscription_id ||
                            mb.icount_subscription_id ||
                            '—'}
                        </span>
                      </InfoField>
                      <InfoField label="OpenSRS Order ID">
                        <span className="font-mono text-xs break-all">
                          {mb.opensrs_order_id || '—'}
                        </span>
                      </InfoField>
                      <InfoField label="Current Period Start">
                        {mb.current_period_start
                          ? new Date(mb.current_period_start).toLocaleString()
                          : '—'}
                      </InfoField>
                      <InfoField label="Current Period End">
                        {mb.current_period_end
                          ? new Date(mb.current_period_end).toLocaleString()
                          : '—'}
                      </InfoField>
                      <InfoField label="Cancel at Period End">
                        {mb.cancel_at_period_end ? (
                          <span className="text-orange-600 font-medium">Yes</span>
                        ) : (
                          <span className="text-gray-500">No</span>
                        )}
                      </InfoField>
                      <InfoField label="Created At">
                        {mb.created_at
                          ? new Date(mb.created_at).toLocaleString()
                          : '—'}
                      </InfoField>
                      {mb.canceled_at && (
                        <InfoField label="Canceled At">
                          {new Date(mb.canceled_at).toLocaleString()}
                        </InfoField>
                      )}
                      <InfoField label="Mailbox ID">
                        <span className="font-mono text-xs break-all">{mb.id}</span>
                      </InfoField>
                    </div>
                    {mb.error_message && (
                      <div className="mt-3 px-3 py-2 rounded bg-red-50 border border-red-200">
                        <p className="text-xs text-red-700 break-all">
                          <span className="font-semibold">Error:</span> {mb.error_message}
                        </p>
                      </div>
                    )}
                    {/* Retry provisioning for mailboxes that never reached
                        active. Hidden for soft-deleted rows (cancellation
                        history) — those should not be re-provisioned. */}
                    {mb.provider_status !== 'deleted'
                      && ['failed', 'payment_confirmed', 'provisioning'].includes(mb.status) && (
                      <div className="mt-3">
                        <button
                          onClick={() => handleRetryMailbox(mb.id)}
                          disabled={Boolean(retryingMailboxId)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg bg-pink-600 text-white hover:bg-pink-700 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          <ArrowPathIcon className={`w-4 h-4 ${retryingMailboxId === mb.id ? 'animate-spin' : ''}`} />
                          {retryingMailboxId === mb.id ? 'Retrying…' : 'Retry provisioning'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Linked Entities */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-1">Linked Entities</h3>
          <p className="text-sm text-gray-500 mb-4">
            Subscriptions are billed to an account and optionally tied to one website. The checkout user is kept for legacy billing webhooks only.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Account */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <BuildingOfficeIcon className="w-5 h-5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">Account</span>
                </div>
                {!linkingAccount && (
                  <button
                    onClick={startLinkingAccount}
                    className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 font-medium"
                    title={subscription.account_id ? 'Change linked account' : 'Link an account'}
                  >
                    <LinkIcon className="w-3.5 h-3.5" />
                    {subscription.account_id ? 'Change' : 'Link'}
                  </button>
                )}
              </div>
              {linkingAccount ? (
                <div className="space-y-3" ref={accountDropdownRef}>
                  <div className="relative">
                    <input
                      type="text"
                      value={accountSearchQuery}
                      onChange={handleAccountSearchChange}
                      onFocus={() => {
                        if (accountSearchResults.length > 0) setShowAccountDropdown(true);
                      }}
                      placeholder="Search by account name or admin email..."
                      className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    {accountSearchLoading && (
                      <p className="text-xs text-gray-400 mt-1">Searching...</p>
                    )}
                    {showAccountDropdown && accountSearchResults.length > 0 && (
                      <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {accountSearchResults.map((account) => (
                          <li key={account.id}>
                            <button
                              type="button"
                              onClick={() => handleSelectAccount(account)}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                            >
                              <span className="font-medium text-gray-900">{account.name || 'Unnamed Account'}</span>
                              {account.tier && (
                                <span className="text-gray-500"> ({account.tier})</span>
                              )}
                              {account.primaryEmail && (
                                <span className="block text-xs text-gray-500">{account.primaryEmail}</span>
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSaveAccountLink}
                      disabled={actionLoading === 'link-account' || !selectedAccount?.id}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors disabled:opacity-50"
                    >
                      <CheckIcon className="w-3.5 h-3.5" />
                      {actionLoading === 'link-account' ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => setLinkingAccount(false)}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-md transition-colors"
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : subscription.account_id ? (
                <div>
                  <Link
                    to={`/accounts/${subscription.account_id}`}
                    className="text-primary-600 hover:text-primary-800 font-medium"
                  >
                    {subscription.account_name || 'View Account'}
                  </Link>
                  {subscription.account_tier && (
                    <p className="text-xs text-gray-500 mt-1">Tier: {subscription.account_tier}</p>
                  )}
                </div>
              ) : (
                <span className="text-gray-400 italic">No account linked</span>
              )}
            </div>

            {/* Website */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <GlobeAltIcon className="w-5 h-5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">Website</span>
                </div>
                {!linkingWebsite && (
                  <button
                    onClick={startLinkingWebsite}
                    className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 font-medium"
                    title={subscription.website_id ? 'Change linked website' : 'Link a website'}
                  >
                    <LinkIcon className="w-3.5 h-3.5" />
                    {subscription.website_id ? 'Change' : 'Link'}
                  </button>
                )}
              </div>
              {linkingWebsite ? (
                <div className="space-y-3">
                  {loadingWebsites ? (
                    <p className="text-sm text-gray-400">Loading websites...</p>
                  ) : accountWebsites.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">No websites found for this account</p>
                  ) : (
                    <>
                      <select
                        value={selectedWebsiteId}
                        onChange={(e) => setSelectedWebsiteId(e.target.value)}
                        className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">None (account-wide)</option>
                        {accountWebsites.map((w) => {
                          const compatible = isWebsiteCompatible(subscription.plan_type, w.site_type);
                          const typeLabel = SITE_TYPE_LABELS[w.site_type || 'marketing'] || w.site_type || 'Marketing';
                          return (
                            <option key={w.id} value={w.id} disabled={!compatible}>
                              {w.name} [{typeLabel}]{w.custom_domain_url ? ` — ${w.custom_domain_url}` : ''}{w.deployment_status === 'deployed' ? ' ✓' : ''}{!compatible ? ' (incompatible)' : ''}
                            </option>
                          );
                        })}
                      </select>
                      {selectedWebsiteId && (() => {
                        const selected = accountWebsites.find((w) => w.id === selectedWebsiteId);
                        if (selected && !isWebsiteCompatible(subscription.plan_type, selected.site_type)) {
                          const planLabel = PLAN_TYPE_LABELS[subscription.plan_type] || subscription.plan_type;
                          const siteLabel = SITE_TYPE_LABELS[selected.site_type || 'marketing'] || selected.site_type;
                          return (
                            <div className="mt-1.5 px-2 py-1.5 rounded bg-red-50 border border-red-200">
                              <p className="text-xs text-red-700 font-medium">
                                Cannot link: "{planLabel}" subscription is not compatible with "{siteLabel}" website.
                              </p>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </>
                  )}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSaveWebsiteLink}
                      disabled={actionLoading === 'link-website' || loadingWebsites || (selectedWebsiteId && (() => {
                        const selected = accountWebsites.find((w) => w.id === selectedWebsiteId);
                        return selected && !isWebsiteCompatible(subscription.plan_type, selected.site_type);
                      })())}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors disabled:opacity-50"
                    >
                      <CheckIcon className="w-3.5 h-3.5" />
                      {actionLoading === 'link-website' ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => setLinkingWebsite(false)}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-md transition-colors"
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : subscription.website_id ? (
                <div>
                  <Link
                    to={`/websites/${subscription.website_id}`}
                    className="text-primary-600 hover:text-primary-800 font-medium"
                  >
                    {subscription.website_name || 'View Website'}
                  </Link>
                  {subscription.website_domain && (
                    <p className="text-sm text-gray-500 mt-1">{subscription.website_domain}</p>
                  )}
                  {subscription.website_site_type && (
                    <p className="text-xs text-gray-500 mt-1">
                      Type: <span className="font-medium">{subscription.website_site_type}</span>
                    </p>
                  )}
                  {subscription.website_site_type && !isWebsiteCompatible(subscription.plan_type, subscription.website_site_type) && (
                    <div className="mt-2 px-2 py-1.5 rounded bg-amber-50 border border-amber-200">
                      <p className="text-xs text-amber-800 font-medium">
                        ⚠️ Mismatch: subscription is <span className="font-bold">{PLAN_TYPE_LABELS[subscription.plan_type] || subscription.plan_type}</span> but website is <span className="font-bold">{SITE_TYPE_LABELS[subscription.website_site_type] || subscription.website_site_type}</span>
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-gray-400 italic">Legacy (account-wide)</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {domainRetryOpen && subscription?.domain_purchase && (
        <DomainPurchaseRetryModal
          purchase={subscription.domain_purchase}
          onClose={() => setDomainRetryOpen(false)}
          onSuccess={fetchSubscription}
        />
      )}
    </AdminLayout>
  );
};

export default SubscriptionDetail;
